// [template-variables] /library/:templateId/variables — review the variables
// (blanks and per-deal values) found in a template. Reached from the Quick
// actions card on TemplateDetail. Gated on template:tag for the template's
// layer; Approve additionally needs template:certify.
import { Link, useParams } from "react-router-dom";
import { Alert, PageHead, PageLoading } from "../components";
import { TemplateVariablesReview } from "../components/templates/TemplateVariablesReview";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { getApiErrorMessage } from "../data/api";
import { useTemplatePermalink } from "../data/templatesQuery";

export function TemplateVariables(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";
  // [draft permalink] Same permalink, same rule as /library/:id: someone who
  // may tag this template sees it in every lifecycle state, so a draft's
  // variables page isn't a 404 the way it used to be.
  const canTagTemplates =
    sessionCan(session, "template:tag", { layer: "L1" }) ||
    sessionCan(session, "template:tag", { layer: "L2" });
  const { data, isLoading, isError, error } = useTemplatePermalink(
    templateId,
    tenantId,
    canTagTemplates
  );

  if (isLoading) return <PageLoading />;
  if (isError || data === undefined) {
    return (
      <Alert label="Couldn't load template" tone="error">
        {getApiErrorMessage(error) ?? error?.message ?? "Template not found."}{" "}
        <Link to="/library">Back to the certified templates</Link>
      </Alert>
    );
  }

  const layer = data.layerSource;
  const ownTenant =
    layer === "L1" || (data.tenantId !== null && data.tenantId === tenantId);
  const canTag = ownTenant && sessionCan(session, "template:tag", { layer });
  const canApprove = ownTenant && sessionCan(session, "template:certify", { layer });
  const title =
    typeof data.title === "string" && data.title.trim().length > 0 ? data.title : data.id;

  return (
    <>
      <PageHead
        eyebrow={<Link to={`/library/${encodeURIComponent(data.id)}`}>← {title}</Link>}
        title="Review variables"
        sub="The blanks and per-deal values a draft fills in. Accept the ones that are real, remove the rest, then approve."
      />
      {canTag ? (
        <TemplateVariablesReview template={data} canApprove={canApprove} />
      ) : (
        <Alert label="Not available" tone="error">
          You don&apos;t have permission to review variables on this template.
        </Alert>
      )}
    </>
  );
}
